import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoGeometrikTolerans } from './FikoGeometrikTolerans';
import * as THREE from 'three';

// GDT Professional System Imports
import {
  GDTCharacteristicType,
  MaterialCondition,
  ToleranceZoneModifier,
  FeatureControlFrameConfig,
  FCFComponent,
  DatumReference,
  requiresDatum,
  isDeprecated
} from '../fiko-gdt/GDTTypes';
import { GDTManager, getGDTManager } from '../fiko-gdt/GDTManager';
import { FeatureControlFrame, createFCF } from '../fiko-gdt/FeatureControlFrame';

/**
 * ====================================================================
 * FIKO GEOMETRIK TOLERANS COMPONENT
 * ====================================================================
 *
 * CATIA-style Geometric Tolerance (GD&T) Dialog
 * Full ASME Y14.5-2018 and ISO 1101:2017 support
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */
@Component({
  selector: 'app-fiko-geometrik-tolerans',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-geometrik-tolerans.component.html',
  styleUrls: ['./fiko-geometrik-tolerans.component.css']
})
export class FikoGeometrikToleransComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // ==========================================
  // TOLERANCE SETTINGS
  // ==========================================
  toleransTipi: GDTCharacteristicType = GDTCharacteristicType.FLATNESS;
  deger: number = 0.01;
  materyal: MaterialCondition = MaterialCondition.NONE;
  toleransZonModifier: ToleranceZoneModifier = ToleranceZoneModifier.NONE;

  // Datum references
  datum1: string = '';
  datum1Materyal: MaterialCondition = MaterialCondition.NONE;
  datum2: string = '';
  datum2Materyal: MaterialCondition = MaterialCondition.NONE;
  datum3: string = '';
  datum3Materyal: MaterialCondition = MaterialCondition.NONE;

  // Appearance
  renk: string = '#FF00FF';
  boyut: number = 12;
  sabitBoyut: boolean = true;

  // Advanced settings
  profesyonelMod: boolean = true; // Use new FCF system
  birlestikTolerans: boolean = false; // Composite tolerance
  birlestikDeger: number = 0.005;
  birlestikDatum1: string = '';
  birlestikDatum2: string = '';

  // ==========================================
  // TOLERANCE TYPES (14 Geometric Characteristics)
  // ==========================================
  toleransTipleri = [
    // Form tolerances (no datum required)
    { value: GDTCharacteristicType.FLATNESS, label: 'Düzlemsellik ⏥', group: 'Biçim' },
    { value: GDTCharacteristicType.STRAIGHTNESS, label: 'Doğrusallık ─', group: 'Biçim' },
    { value: GDTCharacteristicType.CIRCULARITY, label: 'Yuvarlak Şekil ○', group: 'Biçim' },
    { value: GDTCharacteristicType.CYLINDRICITY, label: 'Silindiriklik ⌭', group: 'Biçim' },
    // Profile tolerances
    { value: GDTCharacteristicType.PROFILE_OF_LINE, label: 'Çizgi Profili ⌒', group: 'Profil' },
    { value: GDTCharacteristicType.PROFILE_OF_SURFACE, label: 'Yüzey Profili ⌓', group: 'Profil' },
    // Orientation tolerances (datum required)
    { value: GDTCharacteristicType.PERPENDICULARITY, label: 'Diklik ⊥', group: 'Yönelim' },
    { value: GDTCharacteristicType.ANGULARITY, label: 'Açısallık ∠', group: 'Yönelim' },
    { value: GDTCharacteristicType.PARALLELISM, label: 'Paralellik ∥', group: 'Yönelim' },
    // Location tolerances (datum required)
    { value: GDTCharacteristicType.POSITION, label: 'Konum ⊕', group: 'Konum' },
    { value: GDTCharacteristicType.CONCENTRICITY, label: 'Merkezlilik ◎ ⚠️', group: 'Konum', deprecated: true },
    { value: GDTCharacteristicType.SYMMETRY, label: 'Simetri ⌯ ⚠️', group: 'Konum', deprecated: true },
    // Runout tolerances (datum required)
    { value: GDTCharacteristicType.CIRCULAR_RUNOUT, label: 'Dairesel Kaçıklık ↗', group: 'Kaçıklık' },
    { value: GDTCharacteristicType.TOTAL_RUNOUT, label: 'Toplam Kaçıklık ⇗', group: 'Kaçıklık' }
  ];

  // ==========================================
  // MATERIAL CONDITIONS (ASME Y14.5-2018)
  // ==========================================
  materyalDurumlari = [
    { value: MaterialCondition.NONE, label: 'Yok' },
    { value: MaterialCondition.MMC, label: 'Maksimum Malzeme Ⓜ' },
    { value: MaterialCondition.LMC, label: 'En Az Malzeme Ⓛ' },
    { value: MaterialCondition.RFS, label: 'Boyuttan Bağımsız Ⓢ' },
    { value: MaterialCondition.MMB, label: 'Maksimum Malzeme Sınırı' },
    { value: MaterialCondition.LMB, label: 'En Az Malzeme Sınırı' },
    { value: MaterialCondition.RMB, label: 'Boyuttan Bağımsız Sınır' }
  ];

  // ==========================================
  // TOLERANCE ZONE MODIFIERS
  // ==========================================
  zonModifierlari = [
    { value: ToleranceZoneModifier.NONE, label: 'Yok' },
    { value: ToleranceZoneModifier.DIAMETER, label: 'Çap Ø' },
    { value: ToleranceZoneModifier.SPHERICAL_DIAMETER, label: 'Küresel Çap SØ' },
    { value: ToleranceZoneModifier.RADIUS, label: 'Yarıçap R' },
    { value: ToleranceZoneModifier.SPHERICAL_RADIUS, label: 'Küresel Yarıçap SR' },
    { value: ToleranceZoneModifier.PROJECTED, label: 'Yansıtılmış Ⓟ' },
    { value: ToleranceZoneModifier.FREE_STATE, label: 'Serbest Durum Ⓕ' },
    { value: ToleranceZoneModifier.TANGENT_PLANE, label: 'Teğet Düzlem Ⓣ' },
    { value: ToleranceZoneModifier.UNEQUALLY_DISPOSED, label: 'Eşit Olmayan Ⓤ' },
    { value: ToleranceZoneModifier.STATISTICAL, label: 'İstatistiksel <ST>' }
  ];

  // ==========================================
  // POINT SELECTION
  // ==========================================
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  // Current annotation
  mevcutTolerans: FikoGeometrikTolerans | FeatureControlFrame | null = null;

  // GDT Manager
  private gdtManager: GDTManager | null = null;

  // Predefined colors
  renkler = ['#FF00FF', '#00FFFF', '#FFD700', '#00FF00', '#FF4444', '#FFA500'];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  toleransAyarlariAcik = true;
  datumAyarlariAcik = false;
  gelismisAyarlariAcik = false;
  gorunumAyarlariAcik = false;

  // Validation messages
  validasyonMesajlari: string[] = [];
  deprecationUyarisi: string = '';

  ngOnInit(): void {
    const sahne = this.anaGosterim?.sahneler?.[0];
    if (sahne) {
      this.gdtManager = getGDTManager(sahne);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  // ==========================================
  // POINT SELECTION
  // ==========================================

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    const meshes = sahne.children.filter((obj: any) => obj.isMesh || obj.type === 'Mesh');
    const intersects = raycaster.intersectObjects(meshes, true);

    if (intersects.length > 0) {
      return intersects[0].point.clone();
    }

    // Fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  // ==========================================
  // VALIDATION
  // ==========================================

  /**
   * Check if current tolerance type is deprecated
   */
  onToleransTipiDegisti(): void {
    if (isDeprecated(this.toleransTipi)) {
      this.deprecationUyarisi = `⚠️ ${this.toleransTipi} ASME Y14.5-2018'de kaldırıldı. Position toleransı kullanmanız önerilir.`;
    } else {
      this.deprecationUyarisi = '';
    }
    this.validasyonKontrol();
    this.onizlemeGuncelle();
  }

  /**
   * Check if datum is required
   */
  get datumGerekli(): boolean {
    return requiresDatum(this.toleransTipi);
  }

  /**
   * Validate current settings
   */
  validasyonKontrol(): void {
    this.validasyonMesajlari = [];

    // Datum check
    if (this.datumGerekli && !this.datum1) {
      this.validasyonMesajlari.push('Bu tolerans tipi en az bir datum referansı gerektirir.');
    }

    // Tolerance value check
    if (this.deger <= 0) {
      this.validasyonMesajlari.push('Tolerans değeri pozitif olmalıdır.');
    }

    // Composite tolerance check
    if (this.birlestikTolerans && this.birlestikDeger >= this.deger) {
      this.validasyonMesajlari.push('Birleşik tolerans değeri ana değerden küçük olmalıdır.');
    }
  }

  // ==========================================
  // SETTINGS CHANGES
  // ==========================================

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutTolerans) {
      // FikoGeometrikTolerans has setSabitBoyut method
      if ('setSabitBoyut' in this.mevcutTolerans) {
        (this.mevcutTolerans as FikoGeometrikTolerans).setSabitBoyut(this.sabitBoyut);
      } else {
        // FeatureControlFrame has sabitBoyut property
        (this.mevcutTolerans as FeatureControlFrame).sabitBoyut = this.sabitBoyut;
      }
      this.anaGosterim?.ciz();
    }
  }

  // ==========================================
  // PREVIEW & CREATE
  // ==========================================

  /**
   * Build datum references array
   */
  private buildDatumReferences(): DatumReference[] {
    const refs: DatumReference[] = [];

    if (this.datum1) {
      refs.push({
        letter: this.datum1.toUpperCase(),
        materialCondition: this.datum1Materyal
      });
    }
    if (this.datum2) {
      refs.push({
        letter: this.datum2.toUpperCase(),
        materialCondition: this.datum2Materyal
      });
    }
    if (this.datum3) {
      refs.push({
        letter: this.datum3.toUpperCase(),
        materialCondition: this.datum3Materyal
      });
    }

    return refs;
  }

  /**
   * Build FCF components array
   */
  private buildFCFComponents(): FCFComponent[] {
    const components: FCFComponent[] = [{
      characteristic: this.toleransTipi,
      toleranceValue: this.deger,
      toleranceZoneModifier: this.toleransZonModifier,
      materialCondition: this.materyal,
      datumReferences: this.buildDatumReferences()
    }];

    // Add composite tolerance if enabled
    if (this.birlestikTolerans) {
      const compositeRefs: DatumReference[] = [];
      if (this.birlestikDatum1) {
        compositeRefs.push({ letter: this.birlestikDatum1.toUpperCase() });
      }
      if (this.birlestikDatum2) {
        compositeRefs.push({ letter: this.birlestikDatum2.toUpperCase() });
      }

      components.push({
        characteristic: this.toleransTipi,
        toleranceValue: this.birlestikDeger,
        toleranceZoneModifier: this.toleransZonModifier,
        materialCondition: this.materyal,
        datumReferences: compositeRefs
      });
    }

    return components;
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutTolerans) {
      sahne.remove(this.mevcutTolerans);
      if ('dispose' in this.mevcutTolerans) {
        this.mevcutTolerans.dispose();
      }
    }

    // Validation check
    this.validasyonKontrol();

    if (this.profesyonelMod && this.gdtManager) {
      // Use new professional FCF system
      const config: FeatureControlFrameConfig = {
        position: this.nokta,
        components: this.buildFCFComponents(),
        style: {
          backgroundColor: '#FFFFFF',
          borderColor: this.renk,
          textColor: '#000000',
          fontSize: this.boyut,
          lineWidth: 1.5
        },
        sabitBoyut: this.sabitBoyut
      };

      this.mevcutTolerans = new FeatureControlFrame(sahne, config);
    } else {
      // Use legacy system
      this.mevcutTolerans = new FikoGeometrikTolerans(
        sahne,
        this.nokta,
        this.toleransTipi.toLowerCase(),
        this.deger
      );
    }

    sahne.add(this.mevcutTolerans);
    this.anaGosterim?.ciz();
  }

  /**
   * Add tolerance to scene
   */
  ekle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir nokta seçin!');
      return;
    }

    // Validation check
    this.validasyonKontrol();
    if (this.validasyonMesajlari.length > 0) {
      const devamEt = confirm(
        'Validasyon uyarıları:\n\n' +
        this.validasyonMesajlari.join('\n') +
        '\n\nDevam etmek istiyor musunuz?'
      );
      if (!devamEt) return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    if (this.profesyonelMod && this.gdtManager) {
      // Use GDTManager to add FCF
      const config: FeatureControlFrameConfig = {
        position: this.nokta,
        components: this.buildFCFComponents(),
        style: {
          backgroundColor: '#FFFFFF',
          borderColor: this.renk,
          textColor: '#000000',
          fontSize: this.boyut,
          lineWidth: 1.5
        },
        sabitBoyut: this.sabitBoyut
      };

      const fcf = this.gdtManager.addFCF(config);

      // Add to etiketler group
      const etiket = sahne.fikoEtiketler.ekleGeometrikTolerans();
      etiket.userData['FikoAnnotation'] = fcf;
      etiket.userData['FCF_ID'] = fcf.userData['id'];
      fcf.parent = etiket;
    } else {
      // Use legacy system
      const tolerans = new FikoGeometrikTolerans(
        sahne,
        this.nokta,
        this.toleransTipi.toLowerCase(),
        this.deger
      );

      const etiket = sahne.fikoEtiketler.ekleGeometrikTolerans();
      etiket.userData['FikoAnnotation'] = tolerans;
      tolerans.parent = etiket;
      sahne.add(tolerans);
    }

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.nokta = null;
    if (this.mevcutTolerans) {
      sahne.remove(this.mevcutTolerans);
      this.mevcutTolerans = null;
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutTolerans) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutTolerans);
        if ('dispose' in this.mevcutTolerans) {
          this.mevcutTolerans.dispose();
        }
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Get grouped tolerance types for optgroup display
   */
  getGrupluToleransTipleri(): { grup: string; tipler: typeof this.toleransTipleri }[] {
    const gruplar = new Map<string, typeof this.toleransTipleri>();

    for (const tip of this.toleransTipleri) {
      if (!gruplar.has(tip.group)) {
        gruplar.set(tip.group, []);
      }
      gruplar.get(tip.group)!.push(tip);
    }

    return Array.from(gruplar.entries()).map(([grup, tipler]) => ({ grup, tipler }));
  }

  /**
   * Get zone modifier symbol for display
   */
  getZonModifierSymbol(): string {
    if (this.toleransZonModifier === ToleranceZoneModifier.NONE) {
      return '';
    }
    const found = this.zonModifierlari.find(z => z.value === this.toleransZonModifier);
    if (found && found.label) {
      // Extract symbol from label (last character after space)
      const parts = found.label.split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : '';
    }
    return '';
  }
}
